import { SingleAndMultiSelectFieldOption } from 'module/api/field/types';
import { useTranslation } from 'module/hooks/use-translation';
import { AiRichTextEditor } from 'module/ui/components/atoms/ai/rich-text-editor';
import { AiInputValue } from 'module/ui/components/atoms/ai/types';
import {
  DrawerBreadcrumb,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { Button, Drawer } from '@rocketlane/ui';
import { InputRef } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

type FormPayload = {
  label: string;
  aiGenerated: boolean;
  instructions: AiInputValue;
};
interface Props {
  visible: boolean;
  onClose: VoidFunction;
  onSubmit: (option: SingleAndMultiSelectFieldOption) => void;
  title: ReactNode;
  breadcrumbs: DrawerBreadcrumb[];
  option?: SingleAndMultiSelectFieldOption;
}
export const ManageOptionInstructionsDrawer: FC<Props> = ({
  visible,
  onClose,
  onSubmit,
  title,
  breadcrumbs,
  option,
}) => {
  const inputRef = useRef<InputRef>(null);
  const requiredMessage = useTranslation('input.required');
  const [form] = useForm<FormPayload>();
  const instructions = useMemo(
    () =>
      option != null
        ? {
            content: option.description,
            editedByUser: !option.fieldOptionMeta?.isDescriptionAiGenerated,
          }
        : {
            content: '',
            editedByUser: false,
          },
    [option],
  );

  useEffect(() => {
    if (visible) {
      inputRef.current?.focus();
    }
    form.setFieldsValue({
      instructions: instructions,
      label: option?.label != null ? option.label : '',
    });
  }, [form, instructions, option, visible]);
  const onDone = useCallback(() => {
    form
      .validateFields()
      .then((payload) => {
        if (option != null) {
          onSubmit({
            ...option,
            label: payload.label,
            description: payload.instructions.content,
            fieldOptionMeta: {
              ...option.fieldOptionMeta,
              isAiGenerated:
                payload.aiGenerated ?? option.fieldOptionMeta?.isAiGenerated,
              isDescriptionAiGenerated: !payload.instructions.editedByUser,
            },
          });
        }
      })
      .catch(() => {});
  }, [form, onSubmit, option]);
  const onFormValuesChange = useCallback(
    (payload) => {
      if (payload.label != null) {
        form.setFieldsValue({
          aiGenerated: false,
        });
      }
    },
    [form],
  );

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={484}
      breadcrumb={<DrawerBreadCrumbs breadcrumbs={breadcrumbs} />}
      title={title}
      content={
        <Form layout="vertical" form={form} onValuesChange={onFormValuesChange}>
          <Form.Item
            name="label"
            label={
              <FormattedMessage id="settings.signal_configuration.builder.option_instructions_drawer.fields.option_label.label" />
            }
            rules={[
              {
                required: true,
                message: requiredMessage,
              },
            ]}
          >
            <Input ref={inputRef} />
          </Form.Item>
          <Form.Item
            name="instructions"
            label={
              <FormattedMessage id="settings.signal_configuration.builder.option_instructions_drawer.fields.instructions.label" />
            }
            extra={
              <FormattedMessage id="settings.signal_configuration.builder.option_instructions_drawer.fields.instructions.hint" />
            }
          >
            <AiRichTextEditor />
          </Form.Item>
          <Form.Item name="aiGenerated" hidden />
        </Form>
      }
      footer={
        <>
          <Button onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button variant="primary" onClick={onDone}>
            <FormattedMessage id="done" />
          </Button>
        </>
      }
    />
  );
};
